// @flow strict

import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImageProps } from 'gatsby-plugin-image';

import { AboutUs } from '~components/about-us/about-us';
import { Layout } from '~components/layout/layout-glossary';
import { GlossaryIntro } from '~components/layout/glossary-intro/glossary-intro';
import { PostList } from '~components/glossary-elements/post-list';
import { NewsletterSignup } from '~components/newsletter-signup/newsletter-signup';

import { useLanguageContext } from '~contexts/language-context';
import { createLinkPath } from '~utils/helpers/create-link-path';
import { withApp } from '../app';

import type { Alternates, PostNode } from '~types/post';
import type { GlossaryCategory } from '~types/glossary-category';

/**
 * Type of component properties.
 */
type GlossaryIndexProps = {
  data: {
    allMarkdownRemark: {
      nodes: Array<PostNode>,
    },
    allGlossaryCategoriesJson: {
      nodes: Array<GlossaryCategory>,
    },
    introImage: GatsbyImageProps,
    site: {
      siteMetadata: { domain: string },
    },
  },

  pageContext: {
    alternates: Alternates,
    canonical: string,
  },
};

/**
 * Template of glossary post index page
 *
 * @param {GlossaryIndexProps} props - component props
 *
 * @returns {Node}
 */
const GlossaryIndex = ({ data, pageContext }: GlossaryIndexProps) => {
  const posts = data.allMarkdownRemark.nodes;
  const categories = data.allGlossaryCategoriesJson.nodes;
  const domain = data.site.siteMetadata.domain;

  const { loc } = useLanguageContext();

  // Todo: add meta images for lexicon
  const seoFields = {
    title: loc('_glossary_title'),
    description: loc('_glossary_description'),
    alternates: pageContext.alternates,
    canonical: createLinkPath({
      path: pageContext.canonical,
      domain,
      isAbsolute: true,
    }),
    meta: [],
  };

  return (
    <Layout seoFields={seoFields} alternates={pageContext.alternates}>
      <section className="container">
        <GlossaryIntro
          headline={loc('_glossary_intro_headline')}
          text={loc('_glossary_intro_text')}
          introImage={data.introImage}
        />
      </section>

      <section className="container">
        <PostList posts={posts} categories={categories} />
      </section>

      <section className="container">
        <div className="container">
          <NewsletterSignup />
        </div>
      </section>

      <section className="container">
        <div className="container">
          <AboutUs />
        </div>
      </section>
    </Layout>
  );
};

export default withApp(GlossaryIndex);

// noinspection JSUnusedGlobalSymbols
export const pageQuery = graphql`
  query GlossaryIndex($language: String!) {
    allMarkdownRemark(
      sort: { fields: [frontmatter___date], order: DESC }
      filter: {
        frontmatter: {
          language: { eq: $language }
          category: {
            regex: "/(Customer Service|Chatbot|Artificial Intelligence)/"
          }
        }
        fields: { draft: { eq: false } }
      }
    ) {
      nodes {
        frontmatter {
          title
          category
          permalink
        }
      }
    }

    allGlossaryCategoriesJson(filter: { language: { eq: $language } }) {
      nodes {
        category
        name
        language
        icons {
          active {
            src {
              childImageSharp {
                gatsbyImageData(
                  quality: 100
                  layout: FULL_WIDTH
                  placeholder: NONE
                )
              }
            }
            alt
          }
          inactive {
            src {
              childImageSharp {
                gatsbyImageData(
                  quality: 100
                  layout: FULL_WIDTH
                  placeholder: NONE
                )
              }
            }
            alt
          }
        }
      }
    }

    introImage: file(relativePath: { eq: "lexikon/intro.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH)
      }
    }

    site {
      siteMetadata {
        domain
      }
    }
  }
`;
